import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  VStack,
  useToast,
  FormErrorMessage,
  Alert,
  AlertIcon,
  HStack,
  PinInput,
  PinInputField,
  Link,
  Center,
  Spinner
} from '@chakra-ui/react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import AppHeader from '../components/AppHeader';
import '../types/turnstile.d.ts';

enum ForgotPasswordStep {
  EMAIL,
  TOKEN,
  NEW_PASSWORD
}

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<ForgotPasswordStep>(ForgotPasswordStep.EMAIL);
  
  // Captcha state
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [isCaptchaLoading, setIsCaptchaLoading] = useState(true);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const captchaRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);
  
  const navigate = useNavigate();
  const toast = useToast();

  // Validation states
  const [emailError, setEmailError] = useState('');
  const [tokenError, setTokenError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  // Cleanup function for Turnstile widget
  const cleanupTurnstile = () => {
    // Remove the widget if it exists
    if (window.turnstile && widgetId) {
      try {
        window.turnstile.remove(widgetId);
        console.log('Removed Turnstile widget:', widgetId);
      } catch (e) {
        console.error('Error removing Turnstile widget:', e);
      }
      setWidgetId(null);
    }

    // Clear the container
    if (captchaRef.current) {
      captchaRef.current.innerHTML = '';
    }
  };

  // Load Turnstile script only once
  useEffect(() => {
    // Only load the script if it hasn't been loaded already
    if (scriptLoadedRef.current || document.querySelector('script[src*="turnstile/v0/api.js"]')) {
      scriptLoadedRef.current = true;
      setIsCaptchaLoading(false);
      return;
    }

    console.log('Loading Turnstile script');
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      console.log('Turnstile script loaded successfully');
      scriptLoadedRef.current = true;
      setIsCaptchaLoading(false);
    };
    
    script.onerror = (error) => {
      console.error('Error loading Turnstile script:', error);
      setCaptchaError('Failed to load CAPTCHA. Please refresh the page and try again.');
      setIsCaptchaLoading(false);
    };
    
    document.body.appendChild(script);

    return () => {
      // We don't remove the script on component unmount because other components might need it
    };
  }, []);

  // Initialize Turnstile when script is loaded
  useEffect(() => {
    // Wait until we have both the script loaded and the captcha container
    if (isCaptchaLoading || !window.turnstile || !captchaRef.current || currentStep !== ForgotPasswordStep.EMAIL) {
      return;
    }

    console.log('Script and container ready, initializing Turnstile');
    
    // Add a small delay to ensure DOM is fully rendered
    const initTimer = setTimeout(() => {
      initializeCaptcha();
    }, 150);

    // Cleanup on unmount
    return () => {
      cleanupTurnstile();
      clearTimeout(initTimer);
    };
  }, [isCaptchaLoading, captchaRef.current, currentStep]);

  // Function to initialize the CAPTCHA
  const initializeCaptcha = () => {
    // Clean up any existing widget before creating a new one
    cleanupTurnstile();

    // Check if captchaRef.current exists
    if (!captchaRef.current) {
      console.error('CAPTCHA container not found');
      return;
    }

    console.log('Initializing new Turnstile widget');
    
    try {
      const id = window.turnstile.render(captchaRef.current, {
        sitekey: '0x4AAAAAABHxImw84H-6Scpx',
        callback: function(token: string) {
          console.log('Captcha verified successfully');
          setCaptchaToken(token);
        },
        'error-callback': function() {
          console.error('Captcha verification failed');
          setCaptchaError('CAPTCHA verification failed. Please try again.');
        },
        theme: 'dark'
      });
      
      setWidgetId(id);
      console.log('New Turnstile widget initialized with ID:', id);
    } catch (error) {
      console.error('Error initializing Turnstile:', error);
      setCaptchaError('Error initializing CAPTCHA. Please refresh the page.');
    }
  };

  const validateEmail = () => {
    if (!email) {
      setEmailError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Invalid email format');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validateToken = () => {
    if (!token || token.length !== 6) {
      setTokenError('Please enter the 6-digit code from your email');
      return false;
    }
    setTokenError('');
    return true;
  };

  const validatePassword = () => {
    let isValid = true;
    setPasswordError('');
    setConfirmError('');
    
    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      isValid = false;
    }
    
    if (!confirmPassword) {
      setConfirmError('Please confirm your password');
      isValid = false;
    } else if (password !== confirmPassword) {
      setConfirmError('Passwords do not match');
      isValid = false;
    }
    
    return isValid;
  };

  const handleSendResetEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail()) return;
    
    // Validate captcha
    if (!captchaToken) {
      toast({
        title: 'Captcha Required',
        description: 'Please complete the captcha verification.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    // Store captcha token before resetting
    const currentCaptchaToken = captchaToken;
    
    // Reset captcha for next attempt
    if (window.turnstile && widgetId) {
      window.turnstile.reset(widgetId);
      
      // Queue the token reset for after this function completes
      setTimeout(() => {
        setCaptchaToken(null);
      }, 100);
    }
    
    try {
      // Send password reset email
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/forgot-password?step=token&email=${encodeURIComponent(email)}`,
        captchaToken: currentCaptchaToken
      });
      
      if (error) throw error;
      
      toast({
        title: 'Email sent',
        description: 'Check your email for a password reset link and code',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      setCurrentStep(ForgotPasswordStep.TOKEN);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyToken = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateToken()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Verify OTP token with Supabase
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'recovery'
      });
      
      if (error) throw error;
      
      toast({
        title: 'Code verified',
        description: 'Enter your new password',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setCurrentStep(ForgotPasswordStep.NEW_PASSWORD);
    } catch (err: any) {
      setError(err.message || 'Invalid or expired code. Please try again.');
      setToken('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Update the user's password
      const { error } = await supabase.auth.updateUser({
        password
      });
      
      if (error) throw error;
      
      // Show success message
      toast({
        title: 'Password updated',
        description: 'Your password has been reset successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // Redirect to login
      navigate('/signup');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const step = searchParams.get('step');
    const emailParam = searchParams.get('email');
    
    if (step === 'token') {
      setCurrentStep(ForgotPasswordStep.TOKEN);
      if (emailParam) {
        setEmail(emailParam);
      }
    } else if (step === 'new-password') {
      setCurrentStep(ForgotPasswordStep.NEW_PASSWORD);
    }
  }, []);

  return (
    <Box minH="100vh" bg="gray.900">
      <AppHeader
        playbackState={null}
        layoutMode="split"
        setLayoutMode={() => {}}
        accentColor="#1DB954"
        pageTitle="Reset Password"
        isCondensed={false}
      />
      
      <Container maxW="md" pt={20}>
        <VStack spacing={8} align="stretch">
          <Heading color="white" textAlign="center">Reset Your Password</Heading>
          
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}
          
          {currentStep === ForgotPasswordStep.EMAIL && (
            <form onSubmit={handleSendResetEmail}>
              <VStack spacing={6}>
                <Text color="gray.300" textAlign="center">
                  Enter your email address and we'll send you instructions to reset your password.
                </Text>
                
                <FormControl isRequired isInvalid={!!emailError}>
                  <FormLabel color="white">Email Address</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    bg="gray.800"
                    color="white"
                    borderColor="gray.600"
                    _hover={{ borderColor: 'green.300' }}
                    _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px green.500' }}
                  />
                  {emailError && <FormErrorMessage>{emailError}</FormErrorMessage>}
                </FormControl>
                
                {/* Captcha container */}
                <Center w="100%" py={2} minH="100px">
                  {isCaptchaLoading ? (
                    <VStack>
                      <Spinner size="md" color="white" />
                      <Text color="gray.400" fontSize="xs">Loading captcha...</Text>
                    </VStack>
                  ) : captchaError ? (
                    <Alert status="error" borderRadius="md" bg="rgba(229, 62, 62, 0.1)" border="1px solid" borderColor="red.300">
                      <AlertIcon />
                      <Text fontSize="sm" color="white">{captchaError}</Text>
                    </Alert>
                  ) : (
                    <Box 
                      ref={captchaRef} 
                      className="cf-turnstile-container" 
                      width="100%" 
                      height="100%" 
                      display="flex" 
                      justifyContent="center"
                    />
                  )}
                </Center>
                
                <Button
                  type="submit"
                  colorScheme="green"
                  width="100%"
                  mt={4}
                  isLoading={isLoading}
                  isDisabled={!email || !captchaToken || isCaptchaLoading || !!captchaError}
                >
                  Send Reset Instructions
                </Button>
                
                <Link as={RouterLink} to="/signup" color="green.400" textAlign="center">
                  Back to Sign In
                </Link>
              </VStack>
            </form>
          )}
          
          {currentStep === ForgotPasswordStep.TOKEN && (
            <form onSubmit={handleVerifyToken}>
              <VStack spacing={6}>
                <Text color="gray.300" textAlign="center">
                  Enter the 6-digit code sent to your email address.
                </Text>
                
                <FormControl isRequired isInvalid={!!tokenError}>
                  <FormLabel color="white">6-Digit Code</FormLabel>
                  <HStack justifyContent="center" spacing={2}>
                    <PinInput 
                      value={token} 
                      onChange={setToken} 
                      otp 
                      size="lg"
                    >
                      <PinInputField 
                        bg="gray.800" 
                        color="white" 
                        borderColor="gray.600"
                        _hover={{ borderColor: 'green.300' }}
                        _focus={{ borderColor: 'green.500' }}
                      />
                      <PinInputField 
                        bg="gray.800" 
                        color="white" 
                        borderColor="gray.600"
                        _hover={{ borderColor: 'green.300' }}
                        _focus={{ borderColor: 'green.500' }}
                      />
                      <PinInputField 
                        bg="gray.800" 
                        color="white" 
                        borderColor="gray.600"
                        _hover={{ borderColor: 'green.300' }}
                        _focus={{ borderColor: 'green.500' }}
                      />
                      <PinInputField 
                        bg="gray.800" 
                        color="white" 
                        borderColor="gray.600"
                        _hover={{ borderColor: 'green.300' }}
                        _focus={{ borderColor: 'green.500' }}
                      />
                      <PinInputField 
                        bg="gray.800" 
                        color="white" 
                        borderColor="gray.600"
                        _hover={{ borderColor: 'green.300' }}
                        _focus={{ borderColor: 'green.500' }}
                      />
                      <PinInputField 
                        bg="gray.800" 
                        color="white" 
                        borderColor="gray.600"
                        _hover={{ borderColor: 'green.300' }}
                        _focus={{ borderColor: 'green.500' }}
                      />
                    </PinInput>
                  </HStack>
                  {tokenError && <FormErrorMessage textAlign="center">{tokenError}</FormErrorMessage>}
                </FormControl>
                
                <Button
                  type="submit"
                  colorScheme="green"
                  width="100%"
                  mt={4}
                  isLoading={isLoading}
                >
                  Verify Code
                </Button>
                
                <Link 
                  onClick={() => setCurrentStep(ForgotPasswordStep.EMAIL)} 
                  color="green.400" 
                  textAlign="center"
                  cursor="pointer"
                >
                  Back to Email Entry
                </Link>
              </VStack>
            </form>
          )}
          
          {currentStep === ForgotPasswordStep.NEW_PASSWORD && (
            <form onSubmit={handleSetNewPassword}>
              <VStack spacing={6}>
                <Text color="gray.300" textAlign="center">
                  Set your new password.
                </Text>
                
                <FormControl isRequired isInvalid={!!passwordError}>
                  <FormLabel color="white">New Password</FormLabel>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter a secure password"
                    bg="gray.800"
                    color="white"
                    borderColor="gray.600"
                    _hover={{ borderColor: 'green.300' }}
                    _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px green.500' }}
                  />
                  {passwordError && <FormErrorMessage>{passwordError}</FormErrorMessage>}
                </FormControl>
                
                <FormControl isRequired isInvalid={!!confirmError}>
                  <FormLabel color="white">Confirm New Password</FormLabel>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    bg="gray.800"
                    color="white"
                    borderColor="gray.600"
                    _hover={{ borderColor: 'green.300' }}
                    _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px green.500' }}
                  />
                  {confirmError && <FormErrorMessage>{confirmError}</FormErrorMessage>}
                </FormControl>
                
                <Button
                  type="submit"
                  colorScheme="green"
                  width="100%"
                  mt={4}
                  isLoading={isLoading}
                >
                  Reset Password
                </Button>
              </VStack>
            </form>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default ForgotPassword; 